import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Guide.css';

function KitFormBranding() {
  useEffect(() => {
    // Load Kit script
    const script = document.createElement('script');
    script.src = 'https://f.convertkit.com/ckjs/ck.5.js';
    script.async = true;
    document.head.appendChild(script);

    // Brand override for Kit's injected styles
    function applyBrandStyles() {
      const form = document.querySelector('.formkit-form[data-uid="e753465b00"]');
      if (!form) return false;
      const btn = form.querySelector('.formkit-submit');
      if (btn) {
        btn.style.setProperty('background-color', '#C47B10', 'important');
        btn.style.setProperty('color', '#14120E', 'important');
        btn.style.setProperty('border-radius', '8px', 'important');
        btn.style.setProperty('font-weight', '800', 'important');
        btn.style.setProperty('font-size', '0.9rem', 'important');
        btn.style.setProperty('padding', '16px 28px', 'important');
        btn.style.setProperty('width', '100%', 'important');
        btn.style.setProperty('letter-spacing', '0.04em', 'important');
        const span = btn.querySelector('span');
        if (span && span.textContent.trim() === 'Subscribe') {
          span.textContent = 'Send Me the Guide \u2192';
        }
      }
      const inputs = form.querySelectorAll('.formkit-input');
      inputs.forEach(function(input) {
        input.style.setProperty('background-color', 'rgba(255,255,255,0.06)', 'important');
        input.style.setProperty('border-color', 'rgba(255,255,255,0.15)', 'important');
        input.style.setProperty('color', '#FDFBF8', 'important');
        input.style.setProperty('border-radius', '8px', 'important');
        input.style.setProperty('border-width', '1.5px', 'important');
        input.style.setProperty('border-style', 'solid', 'important');
        input.style.setProperty('font-size', '0.95rem', 'important');
        input.style.setProperty('padding', '13px 16px', 'important');
      });
      const watermark = form.querySelector('.formkit-powered-by-convertkit-container');
      if (watermark) watermark.style.setProperty('display', 'none', 'important');
      return true;
    }

    const observer = new MutationObserver(function() {
      if (applyBrandStyles()) {
        setTimeout(applyBrandStyles, 200);
        setTimeout(applyBrandStyles, 600);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    let attempts = 0;
    const interval = setInterval(function() {
      applyBrandStyles();
      attempts++;
      if (attempts > 30) clearInterval(interval);
    }, 500);

    return () => { observer.disconnect(); clearInterval(interval); };
  }, []);

  return null;
}

export default function Guide() {
  useEffect(() => {
    document.title = "Innovation Readiness Guide — The IMPACT System™";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', "Five questions every superintendent should answer before launching an initiative. Free one-page guide — delivered to your inbox instantly.");
  }, []);
  return (
    <div className="guide-page" data-testid="guide-page">
      <a href="#main-content" className="guide-skip">Skip to main content</a>
      <div className="guide-topo" aria-hidden="true">
        <svg viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 400 Q200 350 400 380 Q600 410 800 360 Q1000 310 1200 350" stroke="#F5C84A" strokeWidth="1.2"/>
          <path d="M0 300 Q250 260 500 285 Q750 310 1000 265 Q1100 250 1200 260" stroke="#F5C84A" strokeWidth=".8"/>
          <path d="M0 500 Q300 480 600 495 Q900 510 1200 488" stroke="#F5C84A" strokeWidth=".7"/>
        </svg>
      </div>
      <KitFormBranding />
      <div className="guide-page-inner">
        <nav className="guide-nav">
          <Link to="/" className="guide-nav-brand" aria-label="The IMPACT System home">
            <div className="guide-nav-mark" aria-hidden="true"><span>I</span></div>
            <div className="guide-nav-wm">IMPACT<em> SYSTEM&trade;</em></div>
          </Link>
        </nav>
        <main id="main-content" className="guide-main">
          <div className="guide-card" data-testid="guide-card">
            <div className="guide-eyebrow">Free &middot; One Page &middot; Instant Delivery</div>
            <h1 className="guide-h1">Five questions to answer<br /><em>before you launch.</em></h1>
            <p className="guide-sub">A single-page reference for superintendents and district leaders. Scannable in 90 seconds. Designed to be on the table before your next planning session.</p>
            <div className="guide-wyg" role="list" aria-label="What you receive">
              <div className="guide-wyg-label">What you receive</div>
              <div className="guide-wyg-item" role="listitem"><div className="guide-wyg-check" aria-hidden="true">{'\u2713'}</div>The one-page guide &mdash; print it, use it at your next session</div>
              <div className="guide-wyg-item" role="listitem"><div className="guide-wyg-check" aria-hidden="true">{'\u2713'}</div>Five diagnostic questions with reflection prompts for your leadership team</div>
              <div className="guide-wyg-item" role="listitem"><div className="guide-wyg-check" aria-hidden="true">{'\u2713'}</div>Delivered to your inbox once you confirm your email</div>
            </div>

            {/* Kit Form — embedded directly, Kit handles confirmation + PDF delivery */}
            <div className="guide-kit-form" data-testid="guide-kit-form">
              <form
                action="https://app.kit.com/forms/9281802/subscriptions"
                className="seva-form formkit-form"
                method="post"
                data-sv-form="9281802"
                data-uid="e753465b00"
                data-format="inline"
                data-version="5"
              >
                <div data-style="clean">
                  <ul className="formkit-alert formkit-alert-error" data-element="errors" data-group="alert"></ul>
                  <div data-element="fields" data-stacked="false" className="seva-fields formkit-fields">
                    <div className="formkit-field">
                      <input
                        className="formkit-input"
                        aria-label="First Name"
                        name="fields[first_name]"
                        placeholder="Your first name"
                        type="text"
                        autoComplete="given-name"
                        data-testid="guide-first-name-input"
                      />
                    </div>
                    <div className="formkit-field">
                      <input
                        className="formkit-input"
                        name="email_address"
                        aria-label="Email Address"
                        placeholder="you@district.org"
                        required
                        type="email"
                        autoComplete="email"
                        data-testid="guide-email-input"
                      />
                    </div>
                    <button data-element="submit" className="formkit-submit" data-testid="guide-submit-btn">
                      <div className="formkit-spinner"><div></div><div></div><div></div></div>
                      <span>Send Me the Guide &rarr;</span>
                    </button>
                  </div>
                  <div className="formkit-powered-by-convertkit-container" style={{display:'none'}}></div>
                </div>
              </form>
            </div>

            <p className="guide-privacy">No account required. Your email is used only to deliver the guide and occasional resources from The IMPACT System&trade;. Unsubscribe any time.</p>
          </div>
        </main>
        <footer className="guide-footer">
          <div className="guide-footer-copy">&copy; 2026 Ember Impact Ventures LLC &middot; Matthew Callison, PhD</div>
          <div className="guide-footer-links">
            <Link to="/">Home</Link>
            <Link to="/assess">Free Diagnostic</Link>
            <a href="mailto:matt@emberimpact.com">Contact</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
